/** Static debug configuration: flag registry, master toggle, persistence, and category queries. */
import { StorageBackend, LocalStorageBackend } from '../systems/StorageBackend';

export interface DebugFlags {
  // Overlays
  hpBars: boolean;
  aggroBars: boolean;
  aiStateLabels: boolean;
  pathfindingLines: boolean;
  searchMemoryMarkers: boolean;
  visionCones: boolean;
  inspector: boolean;
  legend: boolean;
  offscreenIndicators: boolean;
  dangerOverlay: boolean;
  enemySkillOverlay: boolean;
  fistMarker: boolean;
  // Gameplay
  playerGodMode: boolean;
  enemyGodMode: boolean;
  oneHitKill: boolean;
  infiniteStamina: boolean;
  zeroCooldowns: boolean;
  alwaysSucceedSkills: boolean;
  runInStealth: boolean;
  // Logging
  aiLogging: boolean;
  pathfindingLogging: boolean;
  combatLogging: boolean;
  skillLogging: boolean;
  physicsLogging: boolean;
  spawnLogging: boolean;
  // UI
  useTextAttunement: boolean;
  debugContextMenu: boolean;
}

export type DebugFlagKey = keyof DebugFlags;
export type FlagCategory = 'overlay' | 'gameplay' | 'logging' | 'ui';

interface FlagMeta {
  label: string;
  category: FlagCategory;
}

const FLAG_META: Record<DebugFlagKey, FlagMeta> = {
  // Overlays
  hpBars:              { label: 'HP Bars',              category: 'overlay' },
  aggroBars:           { label: 'Aggro Bars',           category: 'overlay' },
  aiStateLabels:       { label: 'AI State Labels',      category: 'overlay' },
  pathfindingLines:    { label: 'Pathfinding Lines',    category: 'overlay' },
  searchMemoryMarkers: { label: 'Search Memory',        category: 'overlay' },
  visionCones:         { label: 'Vision Cones',         category: 'overlay' },
  inspector:           { label: 'Inspector Panel',      category: 'overlay' },
  legend:              { label: 'Legend Bar',            category: 'overlay' },
  offscreenIndicators: { label: 'Offscreen Indicators', category: 'overlay' },
  dangerOverlay:       { label: 'Danger Overlay',       category: 'overlay' },
  enemySkillOverlay:   { label: 'Enemy Skills',         category: 'overlay' },
  fistMarker:          { label: 'Fist Marker',          category: 'overlay' },
  // Gameplay
  playerGodMode:  { label: 'Player God Mode',  category: 'gameplay' },
  enemyGodMode:   { label: 'Enemy God Mode',   category: 'gameplay' },
  oneHitKill:     { label: 'One Hit Kill',      category: 'gameplay' },
  infiniteStamina:{ label: 'Infinite Stamina',  category: 'gameplay' },
  zeroCooldowns:  { label: 'Zero Cooldowns',     category: 'gameplay' },
  alwaysSucceedSkills: { label: 'Always Succeed Skills', category: 'gameplay' },
  runInStealth:        { label: 'Run in Stealth',        category: 'gameplay' },
  // Logging
  aiLogging:          { label: 'AI Logging',          category: 'logging' },
  pathfindingLogging: { label: 'Pathfinding Logging', category: 'logging' },
  combatLogging:      { label: 'Combat Logging',      category: 'logging' },
  skillLogging:       { label: 'Skill Logging',       category: 'logging' },
  physicsLogging:     { label: 'Physics Logging',     category: 'logging' },
  spawnLogging:       { label: 'Spawn Logging',       category: 'logging' },
  // UI
  useTextAttunement: { label: 'Text Attunement UI', category: 'ui' },
  debugContextMenu:  { label: 'Context Menu',       category: 'ui' },
};

const DEFAULT_FLAGS: DebugFlags = {
  hpBars: true, aggroBars: true, aiStateLabels: true,
  pathfindingLines: true, searchMemoryMarkers: true, visionCones: true,
  inspector: true, legend: true, offscreenIndicators: true, dangerOverlay: true, enemySkillOverlay: true, fistMarker: false,
  playerGodMode: true, enemyGodMode: true, oneHitKill: true,
  infiniteStamina: true, zeroCooldowns: true, alwaysSucceedSkills: true, runInStealth: true,
  aiLogging: true, pathfindingLogging: true, combatLogging: true, skillLogging: true, physicsLogging: true, spawnLogging: true,
  useTextAttunement: false,
  debugContextMenu: true,
};

const DISABLED_FLAGS: Readonly<DebugFlags> = Object.fromEntries(
  Object.keys(DEFAULT_FLAGS).map(k => [k, false]),
) as unknown as DebugFlags;

const STORAGE_KEY = 'debug-config';

interface StoredState {
  enabled: boolean;
  flags: Partial<DebugFlags>;
}

/** Manages all debug flags, the master on/off toggle, and localStorage persistence. */
export class DebugConfig {
  private static _enabled = !!(import.meta as any).env?.DEV;
  private static _flags: DebugFlags = { ...DEFAULT_FLAGS };
  private static _firstLoad = false;
  private static backend: StorageBackend = new LocalStorageBackend();

  // --- Master toggle ---

  static get enabled(): boolean {
    return this._enabled;
  }

  /** Set the master debug toggle and persist the change. */
  static setEnabled(on: boolean): void {
    this._enabled = on;
    this.save();
  }

  // --- Flag access ---

  /** Effective flags — all false when master is off. */
  static get flags(): Readonly<DebugFlags> {
    return this._enabled ? this._flags : DISABLED_FLAGS;
  }

  /** Stored flags regardless of master state (for UI display). */
  static get rawFlags(): Readonly<DebugFlags> {
    return this._flags;
  }

  // --- Category queries (respect master toggle) ---

  static get anyOverlay(): boolean {
    if (!this._enabled) return false;
    const f = this._flags;
    return f.hpBars || f.aggroBars || f.aiStateLabels || f.pathfindingLines
      || f.searchMemoryMarkers || f.visionCones || f.inspector || f.legend
      || f.offscreenIndicators || f.dangerOverlay || f.enemySkillOverlay;
  }

  static get anyGameplay(): boolean {
    if (!this._enabled) return false;
    const f = this._flags;
    return f.playerGodMode || f.enemyGodMode || f.oneHitKill
      || f.infiniteStamina || f.zeroCooldowns || f.alwaysSucceedSkills
      || f.runInStealth;
  }

  static get anyLogging(): boolean {
    if (!this._enabled) return false;
    return this._flags.aiLogging || this._flags.pathfindingLogging || this._flags.combatLogging || this._flags.skillLogging || this._flags.physicsLogging || this._flags.spawnLogging;
  }

  static get anyActive(): boolean {
    return this.anyOverlay || this.anyGameplay || this.anyLogging;
  }

  // --- Flag metadata ---

  /** Return the human-readable display label for a flag key. */
  static flagLabel(flag: DebugFlagKey): string {
    return FLAG_META[flag].label;
  }

  /** Return the category ('overlay' | 'gameplay' | 'logging' | 'ui') for a flag key. */
  static flagCategory(flag: DebugFlagKey): FlagCategory {
    return FLAG_META[flag].category;
  }

  /** Return all flag keys belonging to the given category. */
  static flagsByCategory(category: FlagCategory): DebugFlagKey[] {
    return (Object.keys(FLAG_META) as DebugFlagKey[])
      .filter(k => FLAG_META[k].category === category);
  }

  static get allFlags(): DebugFlagKey[] {
    return Object.keys(FLAG_META) as DebugFlagKey[];
  }

  // --- Mutation ---

  /** Toggle a single flag and persist the change. */
  static toggle(flag: DebugFlagKey): void {
    this._flags[flag] = !this._flags[flag];
    this.save();
  }

  /** Set all flags in a category to the same value and persist. */
  static setCategoryAll(category: FlagCategory, value: boolean): void {
    for (const flag of this.flagsByCategory(category)) {
      this._flags[flag] = value;
    }
    this.save();
  }

  /** Reset all flags and the master toggle to defaults, then persist. */
  static resetAll(): void {
    this._flags = { ...DEFAULT_FLAGS };
    this._enabled = !!(import.meta as any).env?.DEV;
    this.save();
  }

  // --- Persistence ---

  /** True when no saved debug config existed (brand new user). Resets after read. */
  static get firstLoad(): boolean {
    const val = this._firstLoad;
    this._firstLoad = false;
    return val;
  }

  /** Load stored debug state from the backend, falling back to defaults on parse error. */
  static load(): void {
    const data = this.backend.read(STORAGE_KEY);
    if (!data) {
      this._firstLoad = true;
      this.save();
      return;
    }

    try {
      const parsed = JSON.parse(data) as Partial<StoredState>;
      if (typeof parsed.enabled === 'boolean') {
        this._enabled = parsed.enabled;
      }
      if (parsed.flags) {
        this._flags = { ...DEFAULT_FLAGS, ...parsed.flags };
      }
    } catch {
      console.error('DebugConfig: corrupt data, resetting');
      this._flags = { ...DEFAULT_FLAGS };
    }
  }

  /** Persist current debug state to the storage backend. */
  static save(): void {
    const state: StoredState = {
      enabled: this._enabled,
      flags: this._flags,
    };
    this.backend.write(STORAGE_KEY, JSON.stringify(state));
  }
}
